import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { CheckCircle, BookOpen, Users, FileText, Calendar, Shield, ArrowRight } from 'lucide-react';

const Section = styled.section`
  padding: 120px 24px;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 8px 16px;
  background: #e0f2fe;
  color: #0284c7;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 48px;
  color: #0f172a;
  margin-bottom: 24px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #64748b;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 40px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.1);
    border-color: #002147;
  }

  &:hover .icon-wrapper {
    transform: scale(1.1) rotate(5deg);
    background: #002147;
    color: white;
  }

  &:hover .example-box {
    background: #f0f9ff;
    border-color: #bae6fd;
    color: #0369a1;
  }

  &:hover .example-icon {
    color: #0284c7;
  }
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  color: #002147;
  transition: transform 0.3s ease, background 0.3s ease, color 0.3s ease;
`;

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
  font-family: 'Space Grotesk', sans-serif;
`;

const CardText = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
  flex-grow: 1;
`;

const Example = styled.div`
  background: #f8fafc;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  color: #334155;
  font-weight: 500;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  transition: all 0.3s ease;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #002147;
    transition: color 0.3s ease;
  }
`;

const CategoryLabel = styled.span`
  position: absolute;
  top: 32px;
  right: 32px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 100px;
`;

const useCases = [
  {
    category: "Admissions",
    title: "Eligibility Checker",
    description: "Automatically qualify leads by asking key criteria before they book a call.",
    icon: CheckCircle,
    example: "Do I need a degree for this TEFL course?"
  },
  {
    category: "Sales",
    title: "Pricing & ROI",
    description: "Handle price objections instantly by explaining value and payment plans.",
    icon: FileText,
    example: "Do you offer monthly installment plans?"
  },
  {
    category: "Advising",
    title: "Course Discovery",
    description: "Guide students to the right program based on their career goals.",
    icon: BookOpen,
    example: "Which course is best for teaching online?"
  },
  {
    category: "Support",
    title: "LMS Support",
    description: "Deflect 40% of tech support tickets related to login and access.",
    icon: Shield,
    example: "I can't login to Moodle to submit my assignment."
  },
  {
    category: "Academics",
    title: "Academic FAQs",
    description: "Answer recurring questions about deadlines, extensions, and grading.",
    icon: Calendar,
    example: "When is the deadline for Assignment 3?"
  },
  {
    category: "Admin",
    title: "Document Collection",
    description: "Automate the chase for passports, transcripts, and ID verification.",
    icon: Users,
    example: "Where do I upload my passport copy?"
  }
];

export const EducationUseCases: React.FC = () => {
  return (
    <Section>
      <Container>
        <Header>
          <Badge>End-to-End Automation</Badge>
          <Title>Built for the Entire Student Lifecycle</Title>
          <Subtitle>
            From the first "Hello" to graduation day, SmushChat handles the conversations that matter most.
          </Subtitle>
        </Header>

        <Grid>
          {useCases.map((useCase, index) => (
            <Card
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <CategoryLabel>{useCase.category}</CategoryLabel>
              <IconWrapper className="icon-wrapper">
                <useCase.icon size={32} strokeWidth={1.5} />
              </IconWrapper>
              <CardTitle>{useCase.title}</CardTitle>
              <CardText>{useCase.description}</CardText>
              <Example className="example-box">
                <ArrowRight size={16} className="example-icon" />
                "{useCase.example}"
              </Example>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default EducationUseCases;
